import React, { useEffect, useState } from 'react';
import { apiHandler } from '../../../api/apihandler';
import { FOLLOWING_LIST, USER_FOLLOW_REQUEST } from '../../../api/constants';
import { apiSucceeded, getAvatarColor, getInitials } from '../feed/feedUtils';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import EmptyState from '../../../components/nemonto/EmptyState';
import './social-list-page.css';

const PersonAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt={name} className="nmsl-avatar" />;
  return (
    <div className="nmsl-avatar" style={{ background: getAvatarColor(name || 'User') }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

const FollowingPage = () => {
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());

  async function loadFollowing() {
    setLoading(true);
    try {
      const res = await apiHandler(FOLLOWING_LIST, 'POST', { page: 1, limit: 100 });
      if (apiSucceeded(res)) setFollowing(res?.data?.data?.followings?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadFollowing(); }, []);

  async function unfollow(userId) {
    if (!userId || busyIds.has(userId)) return;
    setBusyIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        setFollowing((prev) => prev.filter((f) => (f?.following?.id ?? f?.user_id) !== userId));
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">Following</h3>
            {following.length > 0 && <span className="nmsl-count-pill">{following.length}</span>}
          </div>
          <p className="nmsl-subtitle">Creators and cooks you're keeping up with.</p>
        </div>

        <div className="nmsl-main-body">
          {loading && <p className="nmsl-loading">Loading…</p>}
          {!loading && following.length === 0 && (
            <EmptyState icon="how_to_reg" title="Not following anyone yet" sub="Discover cooks and creators worth keeping up with." />
          )}

          {!loading && following.length > 0 && (
            <div className="nmsl-grid">
              {following.map((f) => {
                const user = f?.following;
                // The API doesn't always populate the nested `following`
                // user (comes back null) - the row's own `user_id` is the
                // followed account's id regardless, so fall back to that.
                const targetId = user?.id ?? f?.user_id;
                const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User';
                return (
                  <div key={f.id} className="nmsl-card">
                    <PersonAvatar photo={user?.user_setting?.photo} name={name} />
                    <div className="nmsl-body">
                      <div className="nmsl-name">{name}</div>
                      <div className="nmsl-sub">{f?.followers_count ?? 0} followers · {f?.followings_count ?? 0} following</div>
                    </div>
                    <button
                      type="button"
                      className="nmsl-follow-btn nmsl-following"
                      disabled={busyIds.has(targetId)}
                      onClick={() => unfollow(targetId)}
                    >
                      Unfollow
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default FollowingPage;
